import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { fetchLaunchMe, adminListUsers, LaunchMe, PlanCode, planRank } from "@/api/launch";
import { useSession } from "@/context/SessionContext";

interface LaunchCtx {
  me: LaunchMe | null;
  loading: boolean;
  error: boolean;
  isAdmin: boolean;
  plan: PlanCode | null;          // effective plan (ELITE while trial active)
  basePlan: PlanCode | null;
  isTrial: boolean;
  trialPlan: PlanCode;            // plan granted by the trial (defaults ELITE)
  trialConsumed: boolean;
  trialEndsMs: number | null;     // normalized epoch ms
  secondsRemaining: number | null; // live countdown
  paidSubscription: boolean;
  refetch: () => void;
  hasPlan: (min: PlanCode) => boolean;
  planLabel: (lang: "tr" | "en") => string | null; // canonical label, e.g. "ELITE · Deneme"
}

const Ctx = createContext<LaunchCtx>({
  me: null, loading: false, error: false, isAdmin: false, plan: null, basePlan: null,
  isTrial: false, trialPlan: "ELITE", trialConsumed: false, trialEndsMs: null, secondsRemaining: null,
  paidSubscription: false, refetch: () => {}, hasPlan: () => false, planLabel: () => null,
});

function up(v: unknown): PlanCode | null {
  if (v == null) return null;
  const s = String(v).toUpperCase();
  return (["FREE", "PRO", "ELITE", "PREMIUM"] as const).includes(s as PlanCode) ? (s as PlanCode) : null;
}

// Robust timestamp normalization: accepts unix seconds, unix ms, or ISO string → epoch ms.
function toMs(raw: unknown): number | null {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number") return raw < 1e12 ? raw * 1000 : raw;
  const s = String(raw).trim();
  const n = Number(s);
  if (!isNaN(n) && /^[0-9.]+$/.test(s)) return n < 1e12 ? n * 1000 : n;
  const p = Date.parse(s);
  return isNaN(p) ? null : p;
}

export const LaunchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useSession();
  const [me, setMe] = useState<LaunchMe | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);
  const loadedAtRef = useRef<number>(Date.now());
  const expiredHandledRef = useRef<boolean>(false);

  const load = useCallback(() => {
    if (!isAuthenticated) { setMe(null); setIsAdmin(false); return; }
    setLoading(true); setError(false);
    expiredHandledRef.current = false;
    loadedAtRef.current = Date.now();
    fetchLaunchMe()
      .then((d) => { setMe(d); loadedAtRef.current = Date.now(); })
      .catch(() => { setMe(null); setError(true); })
      .finally(() => setLoading(false));
    adminListUsers().then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
  }, [isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  const endsMs = toMs(me?.trial?.ends_at);
  const serverSecs = typeof me?.trial?.seconds_remaining === "number" ? (me!.trial!.seconds_remaining as number) : null;
  const trialFlag = me?.trial?.active === true;

  // Live countdown. Prefer ends_at; otherwise decay the server-provided seconds from load time.
  useEffect(() => {
    const compute = (): number | null => {
      if (endsMs != null) return Math.max(0, Math.round((endsMs - Date.now()) / 1000));
      if (serverSecs != null) return Math.max(0, Math.round(serverSecs - (Date.now() - loadedAtRef.current) / 1000));
      return null;
    };
    setSecondsRemaining(compute());
    if (endsMs == null && serverSecs == null) return;
    const t = setInterval(() => {
      const v = compute();
      setSecondsRemaining(v);
      // When an active trial reaches zero, refetch once so UI returns to the real effective plan.
      if (trialFlag && v === 0 && !expiredHandledRef.current) {
        expiredHandledRef.current = true;
        load();
      }
    }, 1000);
    return () => clearInterval(t);
  }, [endsMs, serverSecs, trialFlag, load]);

  const effective = up(me?.effective_plan ?? me?.base_plan);
  const basePlan = up(me?.base_plan);
  const isTrial = trialFlag && (secondsRemaining == null || secondsRemaining > 0);
  const trialPlan = up((me?.trial as any)?.plan ?? (me as any)?.trial_plan) ?? "ELITE";
  const plan = isTrial ? (effective ?? trialPlan) : effective;

  const planLabel = (lang: "tr" | "en"): string | null => {
    const base = isTrial ? trialPlan : plan;
    if (!base) return null;
    return isTrial ? `${base} · ${lang === "tr" ? "Deneme" : "Trial"}` : base;
  };

  const value: LaunchCtx = {
    me, loading, error, isAdmin, plan, basePlan,
    isTrial, trialPlan,
    trialConsumed: !!me?.trial?.consumed,
    trialEndsMs: endsMs,
    secondsRemaining,
    paidSubscription: !!me?.paid_subscription,
    refetch: load,
    hasPlan: (min) => planRank(plan) >= planRank(min),
    planLabel,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useLaunch() {
  return useContext(Ctx);
}
